import { useTranslation } from "react-i18next";
import { Button, Input, Select } from "../UI";
import { useTheme } from "styled-components";
import { observer, inject } from "mobx-react";
import { useState } from "react";
import { toBase64, isAcceptedFormat } from "../utils/helper";
import { useInput } from "../utils/hooks";
import { ClipLoader } from "react-spinners";
import ExtentionInsert from "../components/ExtentionInsert";
import ExtentionPreview from "../components/ExtentionPreview";
import LoadingContainer from "../layouts/LoadingContainer";
import PageHeader from "../layouts/PageHeader";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import ErrorNotification from "../components/ErrorNotification";

const UpdateMothReportMobile = ({ monthReportsStore, userStore }) => {
  const { t } = useTranslation("month_reports");
  const theme = useTheme();
  const navigate = useNavigate();
  const accept = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
  const [isLoading, setLoading] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [report, setReport] = useState(monthReportsStore.selectedReport?.data);
  const [abs, setAbs] = useState([]);
  const [absType, setAbsType] = useState(
    monthReportsStore.selectedReport?.CODE
      ? {
          value: monthReportsStore.selectedReport.CODE,
          label: monthReportsStore.selectedReport.DES,
        }
      : null
  );
  const [error, setError] = useState("");
  const [showErrorNotification, setShowErrorNotification] = useState({
    show: false,
    text: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      if (!monthReportsStore.selectedReport) {
        navigate(-1);
      }
      monthReportsStore.setError("");

      const absArray = await monthReportsStore.getAbs();
      const abs = absArray.map((a) => ({
        value: a.CODE,
        label: a.DES,
      }));
      if (!monthReportsStore.isError) {
        setAbs(abs);
      }
      setLoading(false);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!!error) {
      setShowErrorNotification({
        show: true,
        text: error,
      });
    } else {
      setShowErrorNotification({
        show: false,
        text: "",
      });
    }
  }, [error]);

  const vacInput = useInput({
    placeholder: t("vacation"),
    type: "number",
    value: report?.VAC || "",
  });

  const servInput = useInput({
    placeholder: t("service"),
    type: "number",
    value: report?.SERV || "",
  });
  const illInput = useInput({
    placeholder: t("illnace"),
    type: "number",
    value: report?.ILL || "",
  });

  const absInput = useInput({
    value: absType,
  });

  const handleILLChange = (e) => {
    setReport({ ...report, ILL: e.target.value || 0 });
  };
  const handleVACChange = (e) => {
    setReport({ ...report, VAC: e.target.value || 0 });
  };
  const handleSERVChange = (e) => {
    setReport({ ...report, SERV: e.target.value || 0 });
  };

  const handleAbsChange = (value) => {
    setAbsType(value);
    setReport({ ...report, CODE: value?.value });
  };

  const handelUpdate = async () => {
    if (isSaving) return;

    monthReportsStore.setError("");
    const data = {
      ILL: parseFloat(report.ILL),
      VAC: parseFloat(report.VAC),
      SERV: parseFloat(report.SERV),
      CODE: !report?.CODE ? " " : report?.CODE,
      EXTFILENAME: report.EXTFILENAME,
      EXTFILEDES: report.EXTFILEDES,
    };

    const body = {
      CURDATE: monthReportsStore.selectedReport?.data?.CURDATE,
      PERIOD: monthReportsStore.selectedReport?.PERIOD,
      GL: monthReportsStore.selectedReport?.GL,
      ZCR_USERID: userStore.userDetails.ZCR_USERB,
      data: data,
    };
    setSaving(true);
    await monthReportsStore.updateReport(body);

    setSaving(false);
    if (!!monthReportsStore.error) {
      setError(monthReportsStore.error);
    } else {
      navigate("/month_reports");
    }
  };

  const handleSelectedFile = async (e) => {
    e.preventDefault();
    const files = e.target.files;
    isAcceptedFormat(files[0], accept);

    if (!isAcceptedFormat(files[0], accept)) {
      setError(t("error:format_file_type_not_allowed"));
      return;
    }

    if (files.length > 1) {
      setError("error:error_failed_upload_file");
      return;
    }

    try {
      const base64 = await toBase64(files[0]);
      const extentions = [];
      extentions.push({
        EXTFILEDES: files[0].name,
        EXTFILENAME: base64,
      });
      setReport({ ...report, EXTFILENAME: extentions.EXTFILENAME, extentions });
    } catch (err) {
      setError(t("error:error_failed_upload_file"));
    }
  };

  const removeSelectedFile = () => {
    const rep = report;
    delete rep["EXTFILENAME"];
    delete rep["extentions"];
    setReport({ ...rep });
  };

  return (
    <LoadingContainer isLoading={isLoading}>
      <PageHeader title={t("update_report_title")} back={() => navigate(-1)} />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          flex: 0,
          gap: 20,
          width: "100%",
          padding: 20,
          height: `calc( 100vh - 56px)`,
        }}>
        <Input
          style={{ textAlign: "start", width: "100%" }}
          {...vacInput}
          label={t("vacation")}
          onWheel={(e) => e.target.blur()}
          onBlur={(e) => handleVACChange(e)}
        />
        <Input
          style={{ textAlign: "start", width: "100%" }}
          {...illInput}
          label={t("illnace")}
          onWheel={(e) => e.target.blur()}
          onBlur={(e) => handleILLChange(e)}
        />
        <Input
          style={{ textAlign: "start", width: "100%" }}
          {...servInput}
          label={t("service")}
          onWheel={(e) => e.target.blur()}
          onBlur={(e) => handleSERVChange(e)}
        />
        <Select
          style={{ textAlign: "start", width: "100%" }}
          {...absInput}
          onChange={(e) => handleAbsChange(e)}
          options={abs}
          placeholder={t("choose_abs")}
          label={t("choose_abs")}></Select>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 10,
          }}>
          <ExtentionInsert
            singleFile={true}
            extentions={report.extentions}
            onAdd={handleSelectedFile}
          />
          {report.extentions && (
            <ExtentionPreview
              onRemove={removeSelectedFile}
              files={report.extentions}
            />
          )}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 14,
            alignItems: "center",
            padding: 14,
            width: "100%",
            marginBlockStart: "auto",
          }}>
          <Button
            onClick={handelUpdate}
            buttonStyle="main"
            style={{ marginTop: 30 }}>
            {isSaving ? (
              <div style={{ paddingTop: 6 }}>
                <ClipLoader color={theme.color.white} size={22} />
              </div>
            ) : monthReportsStore.selectedExpense?.data !== undefined ? (
              t("update_expense")
            ) : (
              t("add_expense")
            )}
          </Button>
          <Button
            buttonStyle="border-linear"
            onClick={() => navigate(-1)}
            style={{ marginTop: 30 }}>
            {t("common:cancel")}
          </Button>
        </div>
      </div>
      {showErrorNotification.show && (
        <ErrorNotification
          text={showErrorNotification.text}
          onClose={() => setShowErrorNotification({ show: false })}
        />
      )}
    </LoadingContainer>
  );
};

export default inject(
  "monthReportsStore",
  "userStore"
)(observer(UpdateMothReportMobile));
